// External dependencies
import { type FC, useEffect, useRef } from 'react';
import { type Control, Controller, type FieldErrors, useController, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';

// Utilities
import { _isNotBlank, _notNil } from '@/littledash';
import { decimalPlacesValidator, nilOrGreaterThan, nilOrGreaterThanOrEqualTo, valueRequired } from '@/validators';
import { toFiniteNumberOrNull } from '@/utils/Number.utils';

// Model types
import type { TreatmentFormField, TreatmentFormUnit } from '@/components/Modals/AddTreatmentForm/TreatmentForm.model';

// Internal components
import Icon from '@/components/UI/Icon';
import InputWithUnits from '@/components/UI/FormElements/InputWithUnits';

// Relative imports
import Checkbox from '../Checkbox';
import './ControlledInputWithUnits.scss';

interface ControlledInputWithUnitsProps {
  id: string;
  field: TreatmentFormField;
  disabled: boolean;
  hasError: boolean;
  classes: string;
  units: Array<TreatmentFormUnit>;
  /** FieldValues is intentional — field names are built dynamically (e.g. `${id}.default_value`) */
  control: Control;
  errors: FieldErrors;
  selectRequired?: boolean;
  unitsDisabled?: boolean;
  tooltip?: string;
  testId?: string;
  allowZero?: boolean;
  requireValueAtSetup?: boolean;
}

const ControlledInputWithUnits: FC<ControlledInputWithUnitsProps> = ({
  id,
  field,
  disabled = false,
  hasError = false,
  classes,
  units,
  control,
  errors,
  selectRequired,
  unitsDisabled,
  tooltip,
  testId = 'controlled-input-with-units',
  allowZero = false,
  requireValueAtSetup,
}) => {
  const isValueRequired = requireValueAtSetup ?? field.required === true;
  const previousValueRequiredRef = useRef(isValueRequired);
  const { trigger } = useFormContext();
  const { field: defaultValueField } = useController({
    control,
    name: `${id}.default_value`,
    rules: {
      required: isValueRequired ? `${field.label} is required` : undefined,
      validate: {
        valueRequired: valueRequired(field.label, isValueRequired),
        decimalPlacesValidator: decimalPlacesValidator(field.label),
        greaterThan: allowZero
          ? nilOrGreaterThanOrEqualTo(field.label, 0, isValueRequired)
          : nilOrGreaterThan(field.label, 0, isValueRequired),
      },
    },
  });

  useEffect(() => {
    if (previousValueRequiredRef.current === isValueRequired) {
      return;
    }
    previousValueRequiredRef.current = isValueRequired;
    void trigger(`${id}.default_value`);
  }, [id, isValueRequired, trigger]);

  const { field: unitField } = useController({
    control,
    name: `${id}.unit`,
    rules: {
      required: 'Unit is required',
    },
  });

  const selectedUnit = unitField.value as TreatmentFormUnit | undefined;
  const unitName = selectedUnit?.name ?? units[0]?.name;

  const handleValueAndUnitChange = (payload: { value: number | undefined; unit: string | undefined }): void => {
    defaultValueField.onChange(_notNil(payload.value) ? String(payload.value) : '');
    const nextUnit = units.find((unit) => unit.name === payload.unit) ?? units[0];
    if (_notNil(nextUnit)) {
      unitField.onChange(nextUnit);
    }
  };

  const handleBlur = (): void => {
    defaultValueField.onBlur();
    unitField.onBlur();
  };

  return (
    <div data-test-component="ControlledInputWithUnits" data-testid={testId} className="w-100">
      <InputWithUnits
        name={`${id}.default_value`}
        value={toFiniteNumberOrNull(defaultValueField.value)}
        unit={units.length > 1 ? unitName : units[0]?.name}
        onChange={handleValueAndUnitChange}
        onBlur={handleBlur}
        classes={classes}
        disabled={disabled}
        unitsDisabled={unitsDisabled}
        hasError={hasError}
        unitSelectClassName={cn(selectRequired === true ? 'customUnitWidth' : 'unitWidth')}
        {...(units.length > 1
          ? { unitOptions: units.map((unit) => ({ label: unit.name, value: unit.name })) }
          : { unit: units[0]?.name })}
      />
      {selectRequired === true && (
        <div className="pb3 flex justify-between" data-test-element="requied-checkbox-container" data-test-key={id}>
          <Controller
            name={`${id}.required`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox label="Required" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
            )}
          />
          {_isNotBlank(tooltip) && (
            <span data-tooltip-id="global-tooltip-id">
              <Icon className="mid-gray hover-blue" icon="info" width="24" height="24" tooltip={tooltip} />
            </span>
          )}
        </div>
      )}
      <div className="flex flex-column">
        <ErrorMessage
          name={`${id}.default_value`}
          errors={errors}
          render={({ message }) => (
            <small className="red mb3" data-test-element="error" data-test-key={`${id}.default_value`}>
              {message}
            </small>
          )}
        />
        <ErrorMessage
          name={`${id}.unit`}
          errors={errors}
          render={({ message }) => <small className="red mb3">{message}</small>}
        />
      </div>
    </div>
  );
};

export default ControlledInputWithUnits;
